/**
 * Document identity helpers. Documents are addressed locally (and in the cloud
 * mirror — see src/cloud/qdaSync.ts) by their `id` (= fileId): a
 * lowercase-hyphen-number slug that is both the yaqatw plugin/content-control
 * identity AND the storage path segment (see src/data/fs.ts buildPath). The
 * separate `name` is a free-form display label kept inside document.json only.
 * fileIds are unique within a project — that check is what this module provides.
 */
import { fs } from "./fs";
import type { QdaDocument } from "../types";

export interface DocumentSummary {
  /** fileId — folder segment and stable identity. */
  id: string;
  /** Display name, read from document.json (falls back to the id). */
  name: string;
}

/**
 * All fileIds currently in use by documents in a project. Since the folder
 * segment *is* the fileId, this is just the document folder listing. Pass
 * `exceptFileId` to skip one (e.g. the document being re-saved in place).
 */
export async function getUsedFileIds(
  projectName: string,
  exceptFileId?: string
): Promise<Set<string>> {
  const ids = new Set(await fs.listDocuments(projectName));
  if (exceptFileId) ids.delete(exceptFileId);
  return ids;
}

/**
 * List documents as {id, name} pairs for UI (tab labels, the chooser). Reads
 * each document.json for its display name (cheap for realistic project sizes);
 * a doc whose content is missing/corrupt falls back to showing its fileId.
 */
export async function listDocumentSummaries(projectName: string): Promise<DocumentSummary[]> {
  const ids = await fs.listDocuments(projectName);
  const summaries: DocumentSummary[] = [];
  for (const id of ids) {
    const doc = (await fs.readFile(fs.buildPath(projectName, id, "document.json"))) as
      | QdaDocument
      | null;
    summaries.push({ id, name: doc && typeof doc.name === "string" && doc.name ? doc.name : id });
  }
  return summaries;
}

/**
 * Throws a user-facing error if `fileId` is already taken in the project.
 * Callers validate slug *format* separately (isSlug) — this only guards
 * uniqueness against documents already on disk.
 */
export async function assertFileIdAvailable(
  projectName: string,
  fileId: string,
  exceptFileId?: string
): Promise<void> {
  const used = await getUsedFileIds(projectName, exceptFileId);
  if (used.has(fileId)) {
    throw new Error(`File id "${fileId}" is already used by another document in this project.`);
  }
}
